export const CONNECTION_PHASES = [
  { id: 'app', label: 'Запуск приложения', blocking: true },
  { id: 'core', label: 'Ядро клиента', blocking: true },
  { id: 'session', label: 'Сессия игрока', blocking: true },
  { id: 'ticket', label: 'Одноразовый билет', blocking: true },
  { id: 'socket', label: 'Соединение с миром', blocking: true },
  { id: 'snapshot', label: 'Снимок мира', blocking: true },
  { id: 'assets', label: 'Ресурсы мира', blocking: true },
  { id: 'world', label: 'Грид и персонаж', blocking: true },
  { id: 'voice', label: 'Голос (необязательно)', blocking: false },
]

const SAFE_ERRORS = {
  offline: 'Нет соединения с сетью.',
  network_error: 'Не удалось связаться с сервером.',
  network_timeout: 'Сеть не ответила вовремя.',
  session_rejected: 'Не удалось восстановить гостевую сессию.',
  session_migration: 'Не удалось перенести прежнюю гостевую сессию.',
  ticket_rejected: 'Не удалось получить одноразовый билет подключения.',
  ticket_expired: 'Билет подключения истёк или уже использован.',
  ws_rejected: 'Сервер мира отклонил подключение.',
  ws_closed: 'Связь с миром прервана.',
  snapshot_timeout: 'Сервер не прислал снимок мира вовремя.',
  asset_failure: 'Не удалось загрузить обязательные ресурсы мира.',
  unknown: 'Не удалось подключиться к миру.',
  // Отказы сервера при входе. Отличаются от сбоев связи тем, что
  // повторять их бессмысленно: сервер ответил осознанно.
  duplicate_user: 'Вы уже в мире на другом устройстве или вкладке. Эта попытка входа отклонена, прежняя сессия продолжает играть.',
  player_limit: 'Мир заполнен: достигнут предел игроков. Очереди нет — попробуйте войти позже.',
  moderation: 'Модератор мира завершил вашу сессию.',
  kicked: 'Сервер завершил вашу сессию.',
  login_required: 'Чтобы войти в мир, нужно войти в аккаунт — так персонаж и вещи останутся вашими на любом устройстве.',
}

// Коды, по которым автоповтор запрещён: это решение сервера, а не
// временный сбой. Долбиться в отклонённый вход — только мешать.
const NO_RETRY_CODES = new Set(['duplicate_user', 'player_limit', 'moderation', 'kicked', 'session_rejected', 'session_migration', 'login_required'])

// Все коды, которыми сервер отбивает вход или завершает сессию:
// duplicate_user и player_limit — при входе, moderation — выгон
// модератором уже играющего, kicked — запасной вариант.
export function isKickCode(code) {
  return code === 'duplicate_user' || code === 'player_limit' || code === 'moderation' || code === 'kicked'
}

export function allowsAutoRetry(code) {
  return !NO_RETRY_CODES.has(String(code || ''))
}

export function safeConnectionError(code) {
  return { code: SAFE_ERRORS[code] ? code : 'unknown', message: SAFE_ERRORS[code] || SAFE_ERRORS.unknown }
}

export function backoffDelay(attempt, { baseMs = 1000, maxMs = 15000 } = {}) {
  return Math.min(maxMs, baseMs * (2 ** Math.max(0, attempt - 1)))
}

export class ConnectionStateMachine {
  // Таймеры по умолчанию обёрнуты в стрелки: голый setTimeout, вызванный как
  // this.setTimer(...), в браузере падает с Illegal invocation (потерян
  // this=window) — и весь вход в мир виснет без ретрая. Node такой вызов
  // прощает, поэтому тесты слепы к этой поломке.
  constructor({ onChange = () => {}, setTimer = (fn, ms) => setTimeout(fn, ms), clearTimer = id => clearTimeout(id) } = {}) {
    this.onChange = onChange
    this.setTimer = setTimer
    this.clearTimer = clearTimer
    this.retryTimer = null
    this.retryAttempt = 0
    this.reset('idle')
  }

  reset(status = 'idle') {
    this.status = status
    this.error = null
    this.retryAt = null
    // Новая попытка входа снимает залипание кика: игрок сам решил
    // повторить, значит показывать отказ прошлой попытки больше нечего.
    this.kicked = false
    this.phases = Object.fromEntries(CONNECTION_PHASES.map(phase => [phase.id, { status: 'pending', loaded: null, total: null }]))
    this.emit()
  }

  start({ reconnecting = false } = {}) {
    this.clearRetry()
    this.reset(reconnecting ? 'reconnecting' : 'connecting')
  }

  begin(id) {
    if (!this.phases[id] || this.status === 'cancelled') return
    this.phases[id] = { ...this.phases[id], status: 'running' }
    this.emit()
  }

  complete(id) {
    if (!this.phases[id] || this.status === 'cancelled') return
    this.phases[id] = { ...this.phases[id], status: 'complete' }
    this.emit()
  }

  counter(id, loaded, total) {
    if (!this.phases[id] || !Number.isFinite(total) || total < 0) return
    const safeLoaded = Math.max(0, Math.min(total, Number(loaded) || 0))
    this.phases[id] = { status: safeLoaded >= total ? 'complete' : 'running', loaded: safeLoaded, total }
    this.emit()
  }

  optionalUnavailable(id, message = 'Необязательная служба недоступна.') {
    if (!this.phases[id]) return
    this.phases[id] = { ...this.phases[id], status: 'unavailable', message }
    this.emit()
  }

  ready() {
    if (this.status === 'error' || this.status === 'cancelled') return
    this.complete('world')
    this.status = 'ready'
    this.error = null
    this.retryAttempt = 0
    this.emit()
  }

  fail(code) {
    // Кик уже показан игроку — сбой закрытия сокета, прилетающий следом,
    // не должен подменять «вы уже в мире» на «связь прервана».
    if (this.kicked) return
    this.clearRetry()
    this.status = 'error'
    this.error = safeConnectionError(code)
    this.emit()
  }

  // Сервер отклонил вход осознанно (duplicate_user, player_limit).
  // Отдельный вход, а не fail(): состояние залипает до действия игрока,
  // автоповтор запрещён, а последующий onClose ничего не перебивает.
  kick(code) {
    this.clearRetry()
    this.kicked = true
    this.status = 'error'
    this.error = safeConnectionError(isKickCode(code) ? code : 'kicked')
    this.emit()
  }

  cancel() {
    this.clearRetry()
    this.status = 'cancelled'
    this.error = null
    // Отмена — осознанный выход игрока из экрана отказа: залипание кика
    // снимается вместе с причиной, иначе экран остался бы с заголовком
    // кика и пустым текстом.
    this.kicked = false
    this.emit()
  }

  scheduleRetry(callback) {
    // После осознанного отказа сервера повторять нечего.
    if (this.kicked || !allowsAutoRetry(this.error?.code)) return 0
    this.clearRetry()
    const delayMs = backoffDelay(++this.retryAttempt)
    this.retryAt = Date.now() + delayMs
    this.emit()
    this.retryTimer = this.setTimer(() => {
      this.retryTimer = null
      this.retryAt = null
      callback()
    }, delayMs)
    return delayMs
  }

  clearRetry() {
    if (this.retryTimer !== null) this.clearTimer(this.retryTimer)
    this.retryTimer = null
    this.retryAt = null
  }

  snapshot() {
    const blocking = CONNECTION_PHASES.filter(phase => phase.blocking)
    let units = 0
    let indeterminate = false
    for (const phase of blocking) {
      const value = this.phases[phase.id]
      if (value.status === 'complete') units += 1
      else if (value.status === 'running' && Number.isFinite(value.total) && value.total > 0) units += value.loaded / value.total
      else if (value.status === 'running') indeterminate = true
    }
    return {
      status: this.status,
      visible: this.status !== 'ready',
      progress: Math.round((units / blocking.length) * 100),
      indeterminate,
      phases: CONNECTION_PHASES.map(phase => ({ ...phase, ...this.phases[phase.id] })),
      error: this.error,
      retryAt: this.retryAt,
      // Экран показывает отказ сервера иначе, чем сбой связи: без
      // отсчёта автоповтора и с другим текстом.
      kicked: Boolean(this.kicked),
    }
  }

  emit() {
    this.onChange(this.snapshot())
  }

  destroy() {
    this.clearRetry()
    this.onChange = () => {}
  }
}
